import * as Globals from "../../globals";
import {CutOut} from "./cutOut";
import {CutOutPreview} from "./cutOutPreview";
import {Window} from "./window";
import {WindowPreview} from "./windowPreview";
import {buildingGroup, clearStates, currentDialogObject, toggleModeButtons} from "../../main";
import {clearGroup, scene} from "../../sceneManager";
import {findChildrenByName} from "../../utils/threeUtils";
import {enablePreviewInCamera} from "../../utils/layerUtils";
import {InteriorDoorPreview} from "./interiorDoorPreview";
import {ExteriorDoorPreview} from "./exteriorDoorPreview";
import {ExteriorDoor} from "./exteriorDoor";
import {InteriorDoor} from "./interiorDoor";
import {generateMeasurements} from "../measurements/measurementGeneration";
import {controlObjectMaterial} from "../../constants/materialConstants";
import {controlObjectName} from "../../constants/nameConstants";
import { placeExteriorDoor, placeInteriorDoor, placeOpening, placeWindow } from "../../../ui/events/uiEvents";
import { isActiveTour } from "../../tour";

export let cutOutPreview = null;
export let cutOutType = null;
export const cutOutTypes = {
    cutOut: 1, exteriorDoor: 2, interiorDoor: 3, window: 4
};

export const placeableWalls = {
    interior: 1,
    exterior: 2,
    both: 3
};

let existingPreview = null;

//Erstellen einer neuen Vorschau. Erstellung eines neuen Bauteils oder beim ändern eines bestehenden (Angeklickt im Layout)
export function getCutOutPreview(type, width, height) {
    let originalUserData = null;
    let recreatedPreview = false; //Wurde ein bestehendes ControlObject ausgewählt

    //Für Bearbeitung die userData zwischenspeichern. Wird später für die Wiederherstellung von Schnitten benötigt
    if (cutOutPreview !== null) {
        originalUserData = {...cutOutPreview.object.userData};
        recreatedPreview = true;
    }

    if (!recreatedPreview) {
        resetCutOutPreview();
    }

    clearStates(); //Aktuelle Preview entfernen

    if (type === cutOutTypes.cutOut) {
        createCutOutPreview(cutOutTypes.cutOut, width, height, 0.1);
    } else if (type === cutOutTypes.exteriorDoor) {
        createCutOutPreview(cutOutTypes.exteriorDoor, width, height, 0.1);
    } else if (type === cutOutTypes.interiorDoor) {
        createCutOutPreview(cutOutTypes.interiorDoor, width, height, 0.1);
    } else if (type === cutOutTypes.window) {
        createCutOutPreview(cutOutTypes.window, width, height, null);
    }

    //Festlegen, dass Preview auf ein bestehendes Bauteil bezieht
    if (recreatedPreview) {
        cutOutPreview.recreated = recreatedPreview;
    }

    //UserData wieder einfügen
    if (originalUserData !== null) {
        cutOutPreview.object.userData.originalParent = originalUserData.originalParent;
        cutOutPreview.object.userData.sceneMatrix = originalUserData.sceneMatrix;
        cutOutPreview.object.userData.groupMatrix = originalUserData.groupMatrix;
        cutOutPreview.object.userData.type = originalUserData.type;
    }
}

//Erstellung der eigentlichen Preview
function createCutOutPreview(type, width, height, floorOffset) {
    let recreated = false;
    if (cutOutPreview !== null) {
        recreated = cutOutPreview.recreated;
    }
    //Setzte Modus
    if (Globals.activeMode === Globals.modes.cutOut) {
        clearCutOutPreview();
    }
    Globals.setActiveMode(Globals.modes.cutOut);

    //Values auslesen
    width = parseFloat(width);
    height = parseFloat(height);
    floorOffset = parseFloat(floorOffset);
    cutOutType = type;

    //Erstellung des Previewobjekts basierend auf Typ
    if (type === cutOutTypes.cutOut) {
        cutOutPreview = new CutOutPreview(width, height, floorOffset);
    } else if (type === cutOutTypes.exteriorDoor) {
        cutOutPreview = new ExteriorDoorPreview(width, height, floorOffset);
    } else if (type === cutOutTypes.interiorDoor) {
        cutOutPreview = new InteriorDoorPreview(width, height, floorOffset);
    } else if (type === cutOutTypes.window) {
        cutOutPreview = new WindowPreview(width, height, floorOffset);
    }
    cutOutPreview.create();
    cutOutPreview.recreated = recreated;

    //Objekt einfügen und Sichtbarkeit einstellen
    scene.addWithUuid(cutOutPreview.object);
    enablePreviewInCamera();
}

//Wiederherstellung der Preview aus Kontrollelement innerhalb der Szene
export function recreateCutoutPreview(type, object, changeExisting = false) {
    if (Globals.activeMode === Globals.modes.cutOut) {
        clearCutOutPreview();
    }
    Globals.setActiveMode(Globals.modes.cutOut);

    //Erstellung des Previewelements
    if (type === cutOutTypes.cutOut) {
        cutOutPreview = CutOutPreview.createFromControlObject(object);
    } else if (type === cutOutTypes.exteriorDoor) {
        cutOutPreview = ExteriorDoorPreview.createFromControlObject(object);
    } else if (type === cutOutTypes.interiorDoor) {
        cutOutPreview = InteriorDoorPreview.createFromControlObject(object);
    } else if (type === cutOutTypes.window) {
        cutOutPreview = WindowPreview.createFromControlObject(object);
    }

    cutOutPreview.recreate();
    cutOutType = type;

    if (changeExisting) {
        existingPreview = cutOutPreview;
    }
}

//Anpassung der Preview: Positionierung & Kollisionsprüfung
export function adjustCutOutPreview() {
    cutOutPreview.adjustPreview();
}

//Zurücksetzen einer Preview bei Bearbeitungsabbruch
export function resetCutOutPreview() {
    if (existingPreview !== null) {
        clearCutOutPreview();
        cutOutPreview = existingPreview;
        existingPreview = null;
    }
    if (cutOutPreview !== null && cutOutPreview.recreated) {
        let control = cutOutPreview.object;
        let originalParent = buildingGroup.getObjectByProperty("uuid", control.userData.originalParent);

        if (originalParent === undefined) {
            let objs = findChildrenByName(scene, controlObjectName);
            for (let i = 0; i < objs.length; i++) {
                clearGroup(objs[0]);
                objs[0].removeFromParent();
            }
            return;
        }

        //Von Szene in Ursprungsgruppe zurückverschieben
        control.removeFromParent();
        originalParent.addWithUuid(control);
        control.material = controlObjectMaterial;
        //Ursprungsposition und Rotation übernehmen
        let position = control.userData.groupMatrix.position;
        let rotation = control.userData.groupMatrix.rotation;
        control.position.set(position.x, position.y, position.z);
        control.rotation.set(rotation.x, rotation.y, rotation.z);
        control.updateMatrix();
        cutOutPreview = null;
        cutOutType = null;
    }
}

//Entfernen der aktuellen Vorschau
export function clearCutOutPreview() {
    if (cutOutPreview !== null) {
        if (cutOutPreview.recreated && currentDialogObject === null) {
            resetCutOutPreview();
        } else {
            scene.remove(cutOutPreview.object);
            let objs = findChildrenByName(scene, controlObjectName);
            for (let i = 0; i < objs.length; i++) {
                clearGroup(objs[0]);
                objs[0].removeFromParent();
            }
        }
        cutOutPreview = null;
        cutOutType = null;
    }
}

//Aus der aktuellen Preview das tatsächliche Bauteil erzeugen
export function generateCutOut(wallGroup = null, wallMeshName = null, regenerateMeasurements = true) {
    let added;
    let eventType = null;
    let reTriggerFunction;
    if (cutOutType === cutOutTypes.cutOut) {
        eventType = 'cutOut';
        added = new CutOut(cutOutPreview).create(wallGroup, wallMeshName);
        reTriggerFunction = placeOpening;
    } else if (cutOutType === cutOutTypes.exteriorDoor) {
        eventType = 'exteriorDoor';
        added = new ExteriorDoor(cutOutPreview).create(wallGroup, wallMeshName);
        reTriggerFunction = placeExteriorDoor;
    } else if (cutOutType === cutOutTypes.interiorDoor) {
        eventType = 'interiorDoor';
        added = new InteriorDoor(cutOutPreview).create(wallGroup, wallMeshName);
        reTriggerFunction = placeInteriorDoor;
    } else if (cutOutType === cutOutTypes.window) {
        eventType = 'window';
        added = new Window(cutOutPreview).create(wallGroup, wallMeshName);
        reTriggerFunction = placeWindow;
    }

    if (!added) {
        return false;
    }
 
    $('#outerWallBtn').trigger('wallCreated', [eventType]);

    clearCutOutPreview();
    Globals.setActiveMode(Globals.modes.view);
    if (regenerateMeasurements) {
        generateMeasurements().then();
    }
    toggleModeButtons();
    existingPreview = null;

    if (reTriggerFunction !== null && !isActiveTour()) {
        reTriggerFunction();
    }

    return true;
}

export function getCutOutTypeFromString(typeString) {
    if (typeString === "cutOut") {
        return cutOutTypes.cutOut;
    } else if (typeString === "exteriorDoor") {
        return cutOutTypes.exteriorDoor;
    } else if (typeString === "interiorDoor") {
        return cutOutTypes.interiorDoor;
    } else if (typeString === "window") {
        return cutOutTypes.window;
    }

    return null;
}
